import React from 'react';
import {
  FireIcon,
  EMSIcon,
  PoliceIcon,
  AttentionTriangleIcon,
  TrainIcon,
} from '@rsos/assets/icons';
import {
  HAZMAT_RELEASE,
  NO_INCIDENT,
  RAIL_FIRE,
} from '@rsos/base-constants/alertEmergencyTypes';
import {
  BURGLARY,
  SILENT_ALARM,
  HOLDUP,
  OTHER,
  CRASH,
  TRAIN_DERAILMENT,
  ACTIVE_ASSAILANT,
  MEDICAL,
  MOBILE_PANIC,
  CO,
  FIRE,
} from '@rsos/constants/alertEmergencyTypes';
import { DISPATCH_REQUESTED, NEW } from '@rsos/constants/alertStatuses';
import { IconContainer } from './Alert.styles';

/** map appropriate icon and colors to alert emergency types
 * @param {string} type - emergency type name
 * @param {Object} theme - theme object
 * @returns react element
 */
export const mapIcon = (type, theme) => {
  const isTestAlertType = `${type}`.startsWith('TEST_');

  /** all test emergency types get same icons and bg color as their non-test siblings
   * to not extend all mappings by another 12 TEST_ cases, we sanitize the type, remove the `TEST_`
   * and treat the type as the regular
   */
  const sanitizedType = () => {
    if (isTestAlertType) {
      return `${type}`.replace('TEST_', '');
    } else {
      return type;
    }
  };
  const iconColor = theme.colors.background;
  const trainIconColor = isTestAlertType
    ? iconColor
    : theme.colors.trainIncidentAlert?.iconColor || iconColor;

  const getIconBgColor = () => {
    // for RSP if a test alert apply same bg color to all corresponding icons
    if (isTestAlertType) {
      return theme.colors.secondaryText;
    }

    const bg = {
      [BURGLARY]: theme.colors.primaryBaseC2,
      [CO]: theme.colors.heavyWarning,
      [CRASH]: theme.colors.primaryBaseC2,
      [FIRE]: theme.colors.heavyWarning,
      [HOLDUP]: theme.colors.primaryBaseC2,
      [MEDICAL]: theme.colors.success,
      [OTHER]: theme.colors.primaryBaseC2,
      [SILENT_ALARM]: theme.colors.primaryBaseC2,
      [ACTIVE_ASSAILANT]: theme.colors.lightDanger,
      [MOBILE_PANIC]: theme.colors.primaryBaseC2,
      [TRAIN_DERAILMENT]: theme.colors.trainIncidentAlert.iconBg,
      [RAIL_FIRE]: theme.colors.trainIncidentAlert.iconBg,
      [HAZMAT_RELEASE]: theme.colors.trainIncidentAlert.iconBg,
      [NO_INCIDENT]: theme.colors.trainIncidentAlert.iconBg,
    };
    return bg[type];
  };
  const icons = {
    BURGLARY: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon width={15} height={14} color={iconColor} />
      </IconContainer>
    ),
    CO: (
      <IconContainer bg={getIconBgColor()}>
        <FireIcon width={20} height={20} color={iconColor} />
      </IconContainer>
    ),
    CRASH: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon width={15} height={14} color={iconColor} />
      </IconContainer>
    ),
    FIRE: (
      <IconContainer bg={getIconBgColor()}>
        <FireIcon width={20} height={20} color={iconColor} />
      </IconContainer>
    ),
    HOLDUP: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon width={15} height={14} color={iconColor} />
      </IconContainer>
    ),
    MEDICAL: (
      <IconContainer bg={getIconBgColor()}>
        <EMSIcon width={15} height={14} color={iconColor} />
      </IconContainer>
    ),
    OTHER: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon color={iconColor} width={15} height={14} />
      </IconContainer>
    ),
    SILENT_ALARM: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon color={iconColor} width={15} height={14} />
      </IconContainer>
    ),
    ACTIVE_ASSAILANT: (
      <IconContainer bg={getIconBgColor()}>
        <AttentionTriangleIcon height={13} width={24} color={iconColor} />
      </IconContainer>
    ),
    TRAIN_DERAILMENT: (
      <IconContainer bg={getIconBgColor()}>
        <TrainIcon width={15} height={15} color={trainIconColor} />
      </IconContainer>
    ),
    RAIL_FIRE: (
      <IconContainer bg={getIconBgColor()}>
        <TrainIcon width={15} height={15} color={trainIconColor} />
      </IconContainer>
    ),
    HAZMAT_RELEASE: (
      <IconContainer bg={getIconBgColor()}>
        <TrainIcon width={15} height={15} color={trainIconColor} />
      </IconContainer>
    ),
    NO_INCIDENT: (
      <IconContainer bg={getIconBgColor()}>
        <TrainIcon width={15} height={15} color={trainIconColor} />
      </IconContainer>
    ),
    MOBILE_PANIC: (
      <IconContainer bg={getIconBgColor()}>
        <PoliceIcon color={iconColor} width={15} height={14} />
      </IconContainer>
    ),
  };
  return icons[sanitizedType()];
};

/**
 * Introduced for multi-routing handles different scenarios for displaying alerts
 * @param {bool} isSupplementalOnly
 * @param {string} alertStatus
 * @param {string} whoTookAction
 * @returns displayed status
 */
export const displayAlertStatus = (
  isSupplementalOnly,
  alertStatus,
  whoTookAction,
) => {
  const isDispatchRequested = alertStatus === DISPATCH_REQUESTED;
  if (isSupplementalOnly) {
    return '';
  }
  if (isDispatchRequested) {
    return NEW;
  } else {
    return whoTookAction;
  }
};

export const getDisplayAddress = ({ isLocationRestricted, civic }) => {
  const { street_1, street_2 } = civic;

  if (isLocationRestricted) {
    return 'Location Restricted';
  } else {
    return `${street_1 ? street_1 : ''}${street_2 ? `, ${street_2}` : ''}`;
  }
};
